import { useEffect, useState } from 'react';
import { errorColor } from './common/ColorCodes';
import { Box, Grid, TextField, Typography } from '@mui/material';
import {
    CONST_BUSINESS_MAIL,
    CONST_SITE_LABEL,
    MESSAGES, validatePassword
} from './common/Helper';
import './common/Styles.css';
import { CONST_ROUTE_PATH } from '../route/RouteConstants';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import DebugService from '../services/DebugService';
import { LoadingButton } from '@mui/lab';
import ForgotPasswordApiService from '../services/ForgotPasswordApiService';
import LeftImg from './common/LeftImg';

/**
 * Reset Password Component
 * @returns 
 */
export const ResetPassword = () => {
    const [formValues, setFormValues] = useState({
        password: '',
        confirmPassword: ''
    });
    const [errors, setErrors] = useState({ email: '' });
    const [isLoading, setIsLoading] = useState(false);
    const [isButtonDisabled, setIsButtonDisabled] = useState(true);
    const [showSuccess, setShowSuccess] = useState(false);
    const [apiError, setAPIError] = useState('');

    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate()

    /**
     * Handle text input change
     * @param {*} e 
     */
    const handleChange = (e) => {
        setIsButtonDisabled(true)

        const { id, value } = e.target;
        setFormValues({
            ...formValues,
            [id]: value,
        });


        const password = document.getElementById('password')?.value ?? ''
        const confirmPassword = document.getElementById('confirmPassword')?.value ?? ''

        if (password !== '' && confirmPassword !== '') {
            setIsButtonDisabled(false)
        }

        DebugService.log(formValues)
    }

    /**
     * Submit button click handler
     * @param {*} e 
     */
    const handleSubmit = (e) => {
        e.preventDefault();
        setIsLoading(true)
        setAPIError('')
        DebugService.log('Reset Password Component: Reset Password', formValues)
        const { password, confirmPassword } = formValues
        let isValidForm = true
        const newErrors = {}

        if (!validatePassword(password)) {
            isValidForm = false
            newErrors['password'] = MESSAGES.PASSWORD_VALID
            setErrors(newErrors)
        }

        if (!validatePassword(confirmPassword)) {
            isValidForm = false
            newErrors['confirmPassword'] = MESSAGES.PASSWORD_VALID
            setErrors(newErrors)
        }

        if (confirmPassword !== password) {
            isValidForm = false
            newErrors['confirmPassword'] = MESSAGES.PASSWORD_CONFIRM_MATCH
            setErrors(newErrors)
        }

        if (isValidForm) {
            setErrors({})
            setShowSuccess(true)
            setIsLoading(false)

            const requestBody = {
                password: password,
                token: searchParams.get('token') ?? '',
                userId: searchParams.get('userId') ?? ''
            }

            if (requestBody['token'] === '' || requestBody['userId'] === '') {
                DebugService.error('Reset Password Component: Invalid token or user id', requestBody);
                setShowSuccess(false)
                setAPIError('Link is invalid. Please connect to admin.')
            } else {
                ForgotPasswordApiService.resetPassword(requestBody)
                    .then(res => {
                        const data = res['data'] ?? {}
                        const error = res['error'] ?? {}

                        DebugService.log(`Reset Password Component: Reset Response`, res)

                        if (error && error['message']) {
                            setIsLoading(false)
                            setErrors({})
                            setAPIError(error['message'])
                        } else {
                            setIsLoading(false)
                            setErrors({})
                            setShowSuccess(true)
                        }
                    })
                    .catch(err => {
                        DebugService.error('Reset Password Component: API Error caught', requestBody, err);
                    })
                    .finally(() => { })
            }
        } else {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        document.title = `${CONST_SITE_LABEL} | Reset Password`

        if (!searchParams.get('token') || !searchParams.get('userId')) {
            navigate(CONST_ROUTE_PATH.SIGN_IN)
        }
    }, [])

    return (
        showSuccess ? (
            <>
            <Typography variant='h4' color={'#202020'}>Password Reset Successfully!</Typography>
            <Typography className='text' variant='subtitle2'>
                Please sign in&nbsp;
                <Link to={CONST_ROUTE_PATH.SIGN_IN} className='link' title='Click to sign in'>here</Link>
            </Typography>
            </>
        ) : (
            <>
            <Typography variant='h4' color={'#202020'}>Set New Password</Typography>
            <Typography className='text' variant='subtitle2'>
                Please set a password for your account
            </Typography>
            <Box component="form" noValidate onSubmit={handleSubmit}>
                <TextField
                margin="normal"
                    fullWidth
                    className='text-field'
                    size='small'
                    id="password"
                    name='password'
                    label="New Password"
                    type="password"
                    required
                    value={formValues.password}
                    onChange={handleChange}
                    error={Boolean(errors.password)}
                    helperText={errors.password}
                    placeholder='must have 6 characters'
                    sx={{
                        input: {
                            backgroundColor: '#fff', // background of the input field
                            color: '#B1B1B1'            // text color
                        },
                        '& .MuiInputLabel-root': {
                            color: '#B1B1B1'               // default label color
                        },
                        '& .MuiInputBase-root': {
                            backgroundColor: '#fff',  // optional: background for the root input wrapper
                            borderColor: '#DCDBDD'
                        }
                    }}
                />
                <TextField
                    fullWidth
                    className='text-field'
                    size='small'
                    id="confirmPassword"
                    name='confirmPassword'
                    label="Confirm Password"
                    type="password"
                    required
                    value={formValues.confirmPassword}
                    onChange={handleChange}
                    error={Boolean(errors.confirmPassword)}
                    helperText={errors.confirmPassword}
                    placeholder='same as password'
                    sx={{
                        input: {
                            backgroundColor: '#fff', // background of the input field
                            color: '#B1B1B1'            // text color
                        },
                        '& .MuiInputLabel-root': {
                            color: '#B1B1B1'               // default label color
                        },
                        '& .MuiInputBase-root': {
                            backgroundColor: '#fff',  // optional: background for the root input wrapper
                            borderColor: '#DCDBDD'
                        }
                    }}
                />
                <LoadingButton
                    fullWidth
                    loading={isLoading}
                    loadingPosition="end"
                    type='submit'
                    disabled={isButtonDisabled}
                    className='submit-btn'
                >
                    Set Password
                </LoadingButton>
                <Typography color={errorColor}>{apiError}</Typography>
            </Box>
            </>
        )
    )
}

/**
 * Forgot Password Component
 * @returns 
 */
export default function Forgotpassword() {

    const [formValues, setFormValues] = useState({ email: '' });
    const [errors, setErrors] = useState({ email: '' });
    const [isButtonDisabled, setIsButtonDisabled] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [isResetPage, setIsResetPage] = useState(false)
    const [showSuccess, setShowSuccess] = useState(false);

    /**
     * Form validation
     * @returns 
     */
    const validateForm = () => {
        let isValid = true;
        const newErrors = { ...errors };
        for (const key in formValues) {
            if (!formValues[key]) {
                newErrors[key] = getErrorMessage(key);
                isValid = false;
            } else {
                newErrors[key] = '';
            }
        }
        if (isValid) {
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formValues.email)) {
                isValid = false
                newErrors['email'] = MESSAGES.EMAIL_VALID
            }
        }
        setErrors(newErrors);
        return isValid;
    };

    /**
     * Submit button click handler
     * @param {*} e 
     */
    const handleSubmit = (e) => {
        e.preventDefault();
        setShowSuccess(false)
        setIsLoading(true)

        if (validateForm()) {
            DebugService.log('ForgotPassword Component: Request to reset password initiated', formValues);

            const requestBody = {
                email: formValues['email']
            }

            ForgotPasswordApiService.forgotPassword(requestBody)
                .then(res => {
                    DebugService.log(`ForgotPassword Component: Forgot Password API response`, res)

                    if (res['error']) {
                        setErrors({
                            email: res['error']['message']
                        });
                    } else {
                        setErrors({})
                        setShowSuccess(true)
                    }

                    setIsLoading(false)
                })
                .catch(err => {
                    DebugService.error('ForgotPassword Component: Error caught while requesting reset password', formValues, err);
                })
                .finally(() => { })
        } else {
            DebugService.log('ForgotPassword Component: Form is not valid. Please fill out all required fields.');
            setIsLoading(false)
        }
    }

    /**
     * On email input change handler
     * @param {*} e 
     */
    const handleChange = (e) => {
        setIsButtonDisabled(true)

        const { id, value } = e.target;
        setFormValues({
            ...formValues,
            [id]: value,
        });

        if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            setIsButtonDisabled(false)
        }
    }

    /**
     * @description Get error message for requested field name (input id)
     * @param {*} fieldName 
     * @returns 
     */
    const getErrorMessage = (fieldName) => {
        switch (fieldName) {
            case 'email': return MESSAGES.EMAIL_REQ
                break;
            default: return 'Field is required'
                break;
        }
    }

    useEffect(() => {
        document.title = `${CONST_SITE_LABEL} | Forgot Password`

        if (window.location.pathname.includes(CONST_ROUTE_PATH.RESET_PASSWORD)) {
            setIsResetPage(true)
        }
    }, [])

    return (
        <Grid container className='sign-in-container'>

            {/* Left Panel */}
            <Grid item xs={12} lg={6}>
                <LeftImg />
            </Grid>

            {/* Right Panel */}
            <Grid item xs={12} lg={6} className='form-grid'>
                <div className='form-container'>
                    {isResetPage ? (
                        // Reset Password Page
                        <ResetPassword />
                    ) : (
                        // Forgot Password Page
                        <>
                            {showSuccess ? (
                                <>
                                    <Typography variant='h4' color={'#202020'}>Password reset link sent</Typography>
                                    <Typography className='text' variant='subtitle2'> A password reset link was sent to: <Link to={`mailto: ${formValues.email}`} className='link'>{formValues.email}</Link>  </Typography>
                                    <Typography className='text' variant='subtitle2'> If you don't see it in a couple of minutes, check your spam folder or ensure your inbox is not full. It was sent from <Link to={`mailto: ${CONST_BUSINESS_MAIL}`} className='link'>{CONST_BUSINESS_MAIL}</Link> </Typography>
                                </>
                            ) : (
                                <>
                                    <Typography variant='h4' color={'#202020'}>Reset your password</Typography>
                                    <Typography className='text' variant='subtitle2'> Enter the email address associated with your account and we'll send you a link to reset your password</Typography>

                                    <Box component="form" noValidate onSubmit={handleSubmit}>

                                        <TextField
                                            margin="normal"
                                            fullWidth
                                            size='small'
                                            id="email"
                                            name='email'
                                            label="Enter your email address"
                                            variant="outlined"
                                            type="email"
                                            required
                                            value={formValues.email}
                                            onChange={handleChange}
                                            error={Boolean(errors.email)}
                                            helperText={errors.email}
                                            sx={{
                                                input: {
                                                    backgroundColor: '#fff', // background of the input field
                                                    color: '#B1B1B1'            // text color
                                                },
                                                '& .MuiInputLabel-root': {
                                                    color: '#B1B1B1'               // default label color
                                                },
                                                '& .MuiInputBase-root': {
                                                    backgroundColor: '#fff',  // optional: background for the root input wrapper
                                                    borderColor: '#DCDBDD'
                                                }
                                            }}
                                        />
                                        <LoadingButton
                                            fullWidth
                                            loading={isLoading}
                                            loadingPosition="end"
                                            type='submit'
                                            disabled={isButtonDisabled}
                                            className='submit-btn'
                                        >
                                            Submit
                                        </LoadingButton>
                                    </Box>
                                </>
                            )}
                            <Typography variant='caption' className='text'>Don't have an account?&nbsp;<Link to={CONST_ROUTE_PATH.SIGN_UP} className='link'>Sign up</Link></Typography>
                        </>
                    )}
                </div>
            </Grid>
        </Grid>
    )
}
